import React, { useState, useRef, BaseSyntheticEvent } from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { FileEarmarkArrowDown, Upload, XCircle } from "react-bootstrap-icons";
import supportImage from "./img/support-img.png";
import {
  useGetSubjectsQuery,
  useCreateSupportRequestMutation,
} from "../../graphql/support.hooks";
import { CustomToast } from "../../components/toast";

const formatBytes = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

interface FormValues {
  subject: string;
  description: string;
}

const Support = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data } = useGetSubjectsQuery();
  const [createSupportRequest] = useCreateSupportRequestMutation();
  const [showToast, setShowToast] = useState<boolean>(false);
  const clearFileInput = () => {
    setFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clickOnFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: BaseSyntheticEvent) => {
    const file = event.target.files?.[0];
    const maxSizeInBytes = 20 * 1024 * 1024;
    if (!file) return;

    setFile(file);

    if (file.size > maxSizeInBytes) {
      setFileError(t("support.largeFile"));
      return;
    }

    setFileError("");
  };

  const showSupportChat = () => {
    if (!window.HelpCrunch) {
      return;
    }

    window.HelpCrunch("onReady", () => window.HelpCrunch("openChat"));
  };

  const validationSchema = Yup.object({
    subject: Yup.string().required(t("support.subjectRequired")),
    description: Yup.string().required(t("support.descriptionRequired")),
  });

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) => {
    setLoading(true);
    try {
      const input = {
        subjectId: parseInt(values.subject),
        description: values.description,
        file: file || null,
      };
      await createSupportRequest({
        variables: {
          input,
        },
      });

      resetForm();
      clearFileInput();
      setShowToast(true);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="position-relative">
      <div>
        <h1 className="mb-5 fs-2 mt-5 mt-lg-0">{t("menu.support")}</h1>
      </div>

      <div className="bg-white rounded-4">
        <div className="col-lg-10 mx-auto">
          <div className="row px-lg-4 px-3 py-lg-5 py-3">
            <div className="col-lg-7 pe-lg-4">
              <p className="fs-4 fw-bold">{t("support.request")}</p>
              <p className="text-gray mb-4">{t("support.description")}</p>
              <Formik
                initialValues={{ subject: "", description: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ touched, errors }) => (
                  <Form>
                    <div className="form-floating mb-4">
                      <Field
                        as="select"
                        name="subject"
                        className={`form-select ps-3 ${
                          touched.subject && errors.subject ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">{t("support.chooseSubject")}</option>
                        {data?.supportRequestSubjects.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Field>
                      <label className="form-label text-gray ps-3">
                        {t("support.subject")}
                      </label>
                      <ErrorMessage
                        name="subject"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="form-floating mb-5">
                      <Field
                        name="description"
                        as="textarea"
                        maxLength={1000}
                        rows={4}
                        placeholder={t("support.subject")}
                        className={`form-control ps-3 ${
                          touched.description && errors.description
                            ? "is-invalid"
                            : ""
                        }`}
                        style={{
                          minHeight: "100px",
                          overflow: "hidden",
                          resize: "none",
                        }}
                        onInput={(
                          e: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          e.target.style.height = "auto";
                          e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                      />
                      <label className="form-label text-gray ps-3">
                        {t("support.describe")}
                      </label>
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-12 mb-4 mb-md-5">
                      {file === null ? (
                        <button
                          onClick={(e: BaseSyntheticEvent) => {
                            e.preventDefault();
                            clickOnFileInput();
                          }}
                          className="col-12 py-3 mb-1 btn btn-outline-primary bg-primary-light primary-hover d-flex align-items-center justify-content-center"
                        >
                          <Upload />
                          <p className="ms-2 fw-normal mb-0 ">
                            {t("support.upload")}
                          </p>
                        </button>
                      ) : (
                        <div className="d-flex align-items-center justify-content-between col-12 px-3 py-2 rounded-1 mb-1 bg-primary-light border border-primary">
                          <div className="d-flex align-items-center">
                            <FileEarmarkArrowDown
                              className=" me-2"
                              color="#3e4957"
                              size={27}
                            />
                            <div className="d-flex flex-column">
                              <p className="mb-0 text-gray ">{file.name}</p>
                              <p className="mb-0 text-gray fs-7">
                                {formatBytes(file.size)}
                              </p>
                            </div>
                          </div>
                          <XCircle
                            onClick={clearFileInput}
                            className="fs-5 cursor-pointer"
                            color="#3e4957"
                            size={21}
                            role="button"
                          />
                        </div>
                      )}
                      <p
                        className={`${
                          fileError ? "text-danger" : "text-gray"
                        } fs-7 mb-2 mb-md-3`}
                      >
                        {fileError || t("support.size")}
                      </p>
                      <input
                        ref={fileInputRef}
                        className="d-none"
                        type="file"
                        accept="*/*"
                        onChange={handleFileChange}
                      />
                    </div>
                    <div className="col-lg-6 mx-auto">
                      <button
                        type="submit"
                        className="btn btn-primary w-100 text-white fw-bold btn-lg"
                        disabled={loading}
                      >
                        {loading ? t("support.sending") : t("support.send")}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-1 d-lg-flex d-none justify-content-center py-5 align-items-center">
              <div className="border-line"></div>
            </div>
            <div className="d-lg-none mt-4 pt-3 mb-4 pb-2 px-3">
              <div className="border-top "></div>
            </div>
            <div className="col-lg-4 ps-lg-4 pe-lg-0">
              <div className="d-flex flex-column align-items-center justify-content-between h-100">
                <div>
                  <p className="fs-4 fw-bold">{t("support.chat")}</p>
                  <p className="text-gray">{t("support.contact")}</p>
                </div>
                <div className="px-4 px-lg-0 mb-5 mb-lg-0">
                  <img
                    src={supportImage}
                    alt="supportImage"
                    className="img-fluid"
                    loading="lazy"
                  />
                </div>
                <div className="w-100">
                  <button
                    className="btn btn-primary w-100 text-white fw-bold btn-lg"
                    onClick={showSupportChat}
                  >
                    {t("support.start")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomToast
        isShow={showToast}
        setShow={setShowToast}
        message={`${t("support.success")}`}
      />
    </Container>
  );
};

export default Support;
